import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, ArrowLeft, Tag, Share2, Linkedin, Twitter, Copy, ExternalLink } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import DOMPurify from "dompurify";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper function to format article content
const formatArticleContent = (content) => {
  if (!content) return '';
  
  // Check if content already contains HTML tags (but not just links)
  const hasComplexHtml = /<(div|p|h[1-6]|ul|ol|li|br|hr)[^>]*>/i.test(content);
  
  if (hasComplexHtml) {
    // Content already has structural HTML, return as-is
    return content;
  }
  
  // Pre-process: Remove escape backslashes from markdown syntax
  let processedContent = content;
  
  // Handle escaped markdown characters (the backslash escapes them)
  // Order matters - do longer patterns first
  processedContent = processedContent.replace(/\\-\\-\\-/g, '---');  // Escaped horizontal rule
  processedContent = processedContent.replace(/\\###\s/g, '### ');   // Escaped h3
  processedContent = processedContent.replace(/\\##\s/g, '## ');     // Escaped h2
  processedContent = processedContent.replace(/\\#\s/g, '# ');       // Escaped h1
  processedContent = processedContent.replace(/\\\*\\\*/g, '**');    // Escaped bold markers
  processedContent = processedContent.replace(/\\\*/g, '*');         // Escaped single asterisk
  processedContent = processedContent.replace(/\\-\s/g, '- ');       // Escaped bullet point
  
  // Convert markdown bold **text** to <strong>
  processedContent = processedContent.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  
  // Convert markdown italic *text* to <em> (but not inside URLs or already processed)
  processedContent = processedContent.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');
  
  // Convert markdown links [text](url) to <a>
  processedContent = processedContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-royal-blue-800 underline">$1</a>');
  
  // Convert horizontal rules ---
  processedContent = processedContent.replace(/^-{3,}$/gm, '<hr class="my-8 border-zinc-200" />');
  
  // Plain text content - convert to HTML
  // Split by double newlines for paragraphs
  const paragraphs = processedContent.split(/\n\n+/);
  
  return paragraphs
    .map(para => {
      const trimmedPara = para.trim();
      
      // Skip empty paragraphs
      if (!trimmedPara) return '';
      
      // Handle horizontal rules
      if (trimmedPara === '<hr class="my-8 border-zinc-200" />') {
        return trimmedPara;
      }
      
      // Handle headers (lines starting with #)
      if (trimmedPara.startsWith('### ')) {
        return `<h3 class="text-xl font-semibold text-zinc-950 mt-8 mb-4">${trimmedPara.replace(/^###\s*/, '')}</h3>`;
      }
      if (trimmedPara.startsWith('## ')) {
        return `<h2 class="text-2xl font-semibold text-zinc-950 mt-10 mb-4">${trimmedPara.replace(/^##\s*/, '')}</h2>`;
      }
      if (trimmedPara.startsWith('# ')) {
        return `<h1 class="text-3xl font-bold text-zinc-950 mt-10 mb-4">${trimmedPara.replace(/^#\s*/, '')}</h1>`;
      }
      
      // Handle bullet points (including emoji bullets)
      const lines = trimmedPara.split('\n').filter(line => line.trim());
      const isBulletList = lines.every(line => /^[-•🧪📈🔒⚙️🏢🧠🌍📡🔬🔌🔮📬]\s/.test(line.trim()));
      
      if (isBulletList && lines.length > 0) {
        const listItems = lines.map(item => 
          `<li class="mb-2">${item.replace(/^[-•🧪📈🔒⚙️🏢🧠🌍📡🔬🔌🔮📬]\s*/, '')}</li>`
        ).join('');
        return `<ul class="list-disc list-inside space-y-1 my-4 ml-4">${listItems}</ul>`;
      }
      
      // Handle numbered lists
      if (/^\d+\.\s/.test(trimmedPara)) {
        const items = lines.filter(line => /^\d+\.\s/.test(line.trim()));
        if (items.length > 0) {
          const listItems = items.map(item => 
            `<li class="mb-2">${item.replace(/^\d+\.\s*/, '')}</li>`
          ).join('');
          return `<ol class="list-decimal list-inside space-y-1 my-4 ml-4">${listItems}</ol>`;
        }
      }
      
      // Regular paragraph - preserve single line breaks
      const withLineBreaks = trimmedPara.replace(/\n/g, '<br />');
      return `<p class="mb-4 leading-relaxed">${withLineBreaks}</p>`;
    })
    .filter(p => p)
    .join('');
};

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchArticle = useCallback(async function() {
    try {
      const response = await axios.get(API + "/articles/" + slug);
      setArticle(response.data);
      const relatedResponse = await axios.get(API + "/articles", { params: { category: response.data.category, limit: 3 } });
      const filtered = [];
      for (let i = 0; i < relatedResponse.data.length; i++) {
        if (relatedResponse.data[i].slug !== slug) {
          filtered.push(relatedResponse.data[i]);
        }
      }
      setRelatedArticles(filtered.slice(0, 3));
    } catch (error) {
      console.error("Failed to fetch article:", error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(function() {
    fetchArticle();
  }, [fetchArticle]);

  const copyLink = function() {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied!");
  };

  const shareOnLinkedIn = function() {
    window.open("https://www.linkedin.com/sharing/share-offsite/?url=" + encodeURIComponent(window.location.href), "_blank");
  };

  const shareOnTwitter = function() {
    const text = article ? article.title : "";
    window.open("https://twitter.com/intent/tweet?url=" + encodeURIComponent(window.location.href) + "&text=" + encodeURIComponent(text), "_blank");
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-zinc-500">Loading...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="article-not-found">
        <div className="text-center">
          <h1 className="font-display text-3xl font-semibold text-zinc-950 mb-4">Article Not Found</h1>
          <p className="text-zinc-600 mb-6">The article does not exist.</p>
          <Link to="/blog">
            <Button className="bg-royal-blue-800 hover:bg-royal-blue-700">
              <ArrowLeft className="mr-2" size={16} />Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const renderTags = function() {
    const tags = [];
    for (let i = 0; i < article.tags.length; i++) {
      tags.push(<span key={article.tags[i]} className="tag-badge">{article.tags[i]}</span>);
    }
    return tags;
  };

  const renderRelated = function() {
    const items = [];
    for (let i = 0; i < relatedArticles.length; i++) {
      const related = relatedArticles[i];
      items.push(
        <Link key={related.id} to={"/blog/" + related.slug} className="article-card p-6 rounded-sm block" data-testid={"related-article-" + i}>
          <Badge variant="secondary" className="bg-royal-blue-100 text-royal-blue-800 mb-3">{related.category}</Badge>
          <h3 className="font-display text-lg font-semibold text-zinc-950 mb-2 hover:text-royal-blue-800">{related.title}</h3>
          <p className="text-zinc-600 text-sm line-clamp-2">{related.excerpt}</p>
        </Link>
      );
    }
    return items;
  };

  return (
    <div className="pt-20" data-testid="article-page">
      <section className="py-16 md:py-24 hero-gradient grain">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="animate-fade-in-up">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-royal-blue-800 mb-6" data-testid="back-to-blog">
              <ArrowLeft size={16} />Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <Badge variant="secondary" className="bg-royal-blue-100 text-royal-blue-800">{article.category}</Badge>
              {article.is_newsletter && <Badge variant="outline" className="text-zinc-500">Newsletter</Badge>}
            </div>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-zinc-950 mb-6">{article.title}</h1>
            <p className="text-lg text-zinc-600 mb-6">{article.excerpt}</p>
            <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-500">
              <span className="flex items-center gap-1">
                <Calendar size={16} />{new Date(article.published_at).toLocaleDateString()}
              </span>
              <div className="flex items-center gap-2">
                <Tag size={16} />
                <div className="flex flex-wrap gap-2">{renderTags()}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_200px] gap-12">
            <article className="prose-content" data-testid="article-content">
              {article.external_link ? (
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-8 text-center">
                  <ExternalLink className="mx-auto mb-4 text-royal-blue-800" size={48} />
                  <h3 className="font-display text-xl font-semibold text-zinc-950 mb-4">This article is hosted externally</h3>
                  <p className="text-zinc-600 mb-6">Click below to read the full article on LinkedIn.</p>
                  <a href={article.external_link} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-royal-blue-800 hover:bg-royal-blue-700">Read on LinkedIn<ExternalLink className="ml-2" size={16} /></Button>
                  </a>
                </div>
              ) : (
                <div 
                  className="prose-content"
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(formatArticleContent(article.content))
                  }} 
                />
              )}
            </article>

            <aside className="lg:sticky lg:top-24 lg:h-fit">
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-6">
                <h4 className="font-semibold text-zinc-950 mb-4 flex items-center gap-2"><Share2 size={18} />Share</h4>
                <div className="space-y-3">
                  <button onClick={shareOnLinkedIn} className="w-full flex items-center gap-3 p-3 bg-white border border-zinc-200 rounded-sm text-sm text-zinc-700 hover:border-royal-blue-800" data-testid="share-linkedin"><Linkedin size={18} />LinkedIn</button>
                  <button onClick={shareOnTwitter} className="w-full flex items-center gap-3 p-3 bg-white border border-zinc-200 rounded-sm text-sm text-zinc-700 hover:border-royal-blue-800" data-testid="share-twitter"><Twitter size={18} />Twitter</button>
                  <button onClick={copyLink} className="w-full flex items-center gap-3 p-3 bg-white border border-zinc-200 rounded-sm text-sm text-zinc-700 hover:border-royal-blue-800" data-testid="copy-link"><Copy size={18} />Copy Link</button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {relatedArticles.length > 0 && (
        <section className="py-16 md:py-24 section-muted" data-testid="related-articles">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <h2 className="font-display text-2xl font-semibold text-zinc-950 mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {renderRelated()}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
